import { Currency } from 'dinero.js';

export enum FbaTransportVersions {
    V2024 = 'v2024',
}

export enum ShipmentPlan {
    AmazonFbaV2024 = 'Amazon FBA V2024',
    SelfArranged = 'Self Arranged',
    WalmartWfs = 'Walmart WFS',
}

export enum FbaTransportV2024ShipmentGroup {
    PartneredSmallParcel = 'PARTNERED_GROUND_SMALL_PARCEL',
    PartneredLTL = 'PARTNERED_FREIGHT_LTL',
    NonPartneredSmallParcel = 'NON_PARTNERED_GROUND_SMALL_PARCEL',
    NonPartneredLTL = 'NON_PARTNERED_FREIGHT_LTL',
}

export enum FbaTransportPlanStatus {
    Active = 'ACTIVE',
    Voided = 'VOIDED',
    Shipped = 'SHIPPED',
    Errored = 'ERRORED',
}

export interface TransportDimensions {
    lengthMm: number
    widthMm: number
    heightMm: number
    weightGm: number
}

export interface LocaleTransportDimensions {
    length: number
    width: number
    height: number
    weight: number
}

export interface FbaTransportAddress {
    city: string | null
    countryCode: string
    postalCode: string
    name: string
    addressLine1: string
    addressLine2?: string | null
    stateOrProvinceCode: string | null
    districtOrCounty: string | null
}

export interface FbaTransportBoxGroup extends TransportDimensions {
    id: string
    boxNumbers: number[]
    items: FbaTransportBoxGroupItem[]
    packingGroupId: string
}

export interface FbaTransportBoxGroupItem {
    id: string
    createdAt: string
    planItemId: string
    msku: string
    quantity: number
}

export interface PalletDetails extends TransportDimensions {
    repeat: number
    stackable: boolean
}

export interface FbaTransportV2024Pallet extends PalletDetails {
    id: string
}

export enum LabelOwner {
    Amazon = 'AMAZON',
    Seller = 'SELLER',
    None = 'NONE',
}

export enum PrepOwner {
    Amazon = 'AMAZON',
    Seller = 'SELLER',
    None = 'NONE',
}

export interface FbaTransportV2024Currency {
    amount: string
    code: Currency
}

export interface FbaTransportV2024PrepInstruction {
    fee: FbaTransportV2024Currency
    prepOwner: PrepOwner
    prepType: 'ITEM_LABELING' | 'ITEM_BUBBLEWRAP' | 'ITEM_POLYBAGGING' | 'ITEM_TAPING' | 'ITEM_BLACK_SHRINKWRAP' | 'ITEM_HANG_GARMENT' | 'ITEM_BOXING' | 'ITEM_SETCREAT' | 'ITEM_RMOVHANG' | 'ITEM_SUFFOSTK' | 'ITEM_CAP_SEALING' | 'ITEM_DEBUNDLE' | 'ITEM_SETSTK' | 'ITEM_SIOC' | 'ITEM_NO_PREP' | 'ADULT' | 'BABY' | 'TEXTILE' | 'HANGER' | 'FRAGILE' | 'LIQUID' | 'SHARP' | 'SMALL' | 'PERFORATED' | 'GRANULAR' | 'SET' | 'FC_PROVIDED' | 'UNKNOWN' | 'NONE'
}

export interface FbaTransportV2024CreatePlanItem {
    expiration: string | null
    prepOwner: PrepOwner
    labelOwner: LabelOwner
    msku: string
    quantity: number
    manufacturingLotCode: string | null
}

export interface FbaTransportV2024Incentive {
    description: string
    target: string
    type: 'FEE' | 'DISCOUNT'
    value: FbaTransportV2024Currency
}

export interface FbaTransportV2024ShippingConfiguration {
    shippingMode: 'GROUND_SMALL_PARCEL' | 'FREIGHT_LTL' | 'FREIGHT_FTL_PALLET' | 'FREIGHT_FTL_NONPALLET' | 'OCEAN_LCL' | 'OCEAN_FCL' | 'AIR_SMALL_PARCEL' | 'AIR_SMALL_PARCEL_EXPRESS' | null
    shippingSolution: 'AMAZON_PARTNERED_CARRIER' | 'USE_YOUR_OWN_CARRIER' | null
}

export interface FbaTransportV2024PackingGroupItem {
    id: string
    planItemId: string
    quantity: number
}

export interface FbaTransportV2024PackingGroup {
    id: string
    items: FbaTransportV2024PackingGroupItem[]
}

export interface FbaTransportV2024PackingOption {
    id: string
    discounts: FbaTransportV2024Incentive[]
    expiration: string | null
    fees: FbaTransportV2024Incentive[]
    packingOptionId: string
    packingGroups: FbaTransportV2024PackingGroup[]
    status: 'OFFERED' | 'ACCEPTED' | 'EXPIRED'
    supportedShippingConfigurations: FbaTransportV2024ShippingConfiguration[]
    confirmedAt: string | null
}

export interface FbaTransportV2024PlanItem {
    id: string
    quantity: number
    expiration: string | null
    createdAt: string
    updatedAt: string
    msku: string
    fnsku: string | null
    asin: string | null
    labelOwner: LabelOwner
    prepOwner: PrepOwner
    manufacturingLotCode: string | null
}

export interface FbaTransportV2024PlacementOption {
    id: string
    discounts: FbaTransportV2024Incentive[]
    expiration: string | null
    fees: FbaTransportV2024Incentive[]
    shipmentIds: string[]
    shipments: FbaTransportV2024Shipment[]
    placementOptionId: string
    status: 'OFFERED' | 'ACCEPTED' | 'EXPIRED'
    confirmedAt: string | null
    isSelected: boolean
}

export interface FbaTransportV2024DeliveryWindowOption {
    id: string
    createPlanOperationId?: string
    deliveryWindowOptionId: string
    shipmentId: string
    availabilityType: string
    discounts: FbaTransportV2024Incentive[]
    startDate: string
    endDate: string
    validUntil: string
    confirmedAt: string | null
}

export interface FbaTransportV2024TransportationOption {
    id: string
    createdAt: string
    createPlanOperationId?: string
    transportationOptionId: string
    shipmentId: string
    carrier: {
        alphaCode: string | null
        name: string | null
    }
    carrierAppointment?: {
        startTime: string
        endTime: string
    } | null
    shippingMode: string
    shippingSolution: string
    preconditions: string[]
    quote?: {
        cost: {
            amount: string
            code: string
        }
    } | null
    confirmedAt: string | null
}

export interface FbaTransportV2024Plan {
    id: string
    createdAt: string
    outboundShipmentId: number
    channelId: number
    name: string
    shipFromAddress: FbaTransportAddress
    marketplace: AmazonMarketplace
    inboundPlanId: string
    isPackingInformationSet: boolean
    isPackingInfoKnown: boolean | null
    createPlanOperationId: string
    packingOptions: FbaTransportV2024PackingOption[]
    boxGroups: FbaTransportBoxGroup[]
    isMarkedAsDeleted?: boolean
    placementOptions: FbaTransportV2024PlacementOption[]
    isCanceled: boolean
    using2dBarcodes: boolean
    status: FbaTransportPlanStatus
    items: FbaTransportV2024PlanItem[]
}

export enum OwnerConstraint {
    AmazonOnly = 'AMAZON_ONLY',
    SellerOnly = 'SELLER_ONLY',
    NoneOnly = 'NONE_ONLY',
}

export enum PrepCategory {
    Adult = 'ADULT',
    Baby = 'BABY',
    FcProvided = 'FC_PROVIDED',
    Fragile = 'FRAGILE',
    Granular = 'GRANULAR',
    Hanger = 'HANGER',
    Liquid = 'LIQUID',
    Perforated = 'PERFORATED',
    Set = 'SET',
    Sharp = 'SHARP',
    Small = 'SMALL',
    Textile = 'TEXTILE',
    Unknown = 'UNKNOWN',
    None = 'NONE',
}

export enum PrepType {
    ItemLabeling = 'ITEM_LABELING',
    ItemBubblewrap = 'ITEM_BUBBLEWRAP',
    ItemPolybagging = 'ITEM_POLYBAGGING',
    ItemTaping = 'ITEM_TAPING',
    ItemBlackShrinkwrap = 'ITEM_BLACK_SHRINKWRAP',
    ItemHangGarment = 'ITEM_HANG_GARMENT',
    ItemBoxing = 'ITEM_BOXING',
    ItemSetcreat = 'ITEM_SETCREAT',
    ItemRmovhang = 'ITEM_RMOVHANG',
    ItemSuffostk = 'ITEM_SUFFOSTK',
    ItemCapSealing = 'ITEM_CAP_SEALING',
    ItemDebundle = 'ITEM_DEBUNDLE',
    ItemSetstk = 'ITEM_SETSTK',
    ItemSioc = 'ITEM_SIOC',
    ItemNoPrep = 'ITEM_NO_PREP',
}

// Amazon also reports category-like values in an item's prep types, so they are valid without being selectable prep types.
export type FbaPrepType = PrepType | PrepCategory;

export interface FbaTransportV2024ItemPrepDetail {
    channelId: number
    msku: string
    allOwnersConstraint: 'MUST_MATCH' | null
    labelOwnerConstraint: OwnerConstraint | null
    prepOwnerConstraint: OwnerConstraint | null
    prepCategory: PrepCategory
    prepTypes: FbaPrepType[]
}

export interface ItemPrepDetailInput {
    msku: string
    prepCategory: PrepCategory
    prepTypes: FbaPrepType[]
}

export interface SkuError {
    sku: string
    reason: string
    isResolvable?: boolean
}

export enum FbaTransportV2024OperationStatus {
    Success = 'SUCCESS',
    InProgress = 'IN_PROGRESS',
    Failed = 'FAILED',
}

export interface FbaTransportV2024OperationProblem {
    code: string
    details: string | null
    message: string
    severity: string
}

export interface FbaTransportV2024Operation {
    id: string
    createdAt: string
    updatedAt: string
    channelId: number
    operationId: string
    status: FbaTransportV2024OperationStatus
    description: string | null
    problems: FbaTransportV2024OperationProblem[] | null
}

export interface FbaTransportV2024Label {
    id: string
    createdAt: string
    updatedAt: string
    name: string
    url: string
    type: 'box' | 'pallet' | 'package' | 'bill_of_lading'
    paperSize: 'US_Letter' | 'PackageLabel_Letter_2' | 'PackageLabel_A4_4' | '4x6'
}

export enum FbaTransportV2024UnitOfMeasurement {
    CM = 'CM',
    IN = 'IN',
}

export enum FbaTransportV2024UnitOfWeight {
    LB = 'LB',
    KG = 'KG',
}

export enum FbaTransportV2024BoxContentInformationSource {
    BARCODE_2D = 'BARCODE_2D',
    MANUAL_PROCESS = 'MANUAL_PROCESS',
    BOX_CONTENT_PROVIDED = 'BOX_CONTENT_PROVIDED',
}

export enum FbaTransportV2024ShipmentFreightClass {
    NONE = 'NONE',
    FC_50 = 'FC_50',
    FC_55 = 'FC_55',
    FC_60 = 'FC_60',
    FC_65 = 'FC_65',
    FC_70 = 'FC_70',
    FC_77_5 = 'FC_77_5',
    FC_85 = 'FC_85',
    FC_92_5 = 'FC_92_5',
    FC_100 = 'FC_100',
    FC_110 = 'FC_110',
    FC_125 = 'FC_125',
    FC_150 = 'FC_150',
    FC_175 = 'FC_175',
    FC_200 = 'FC_200',
    FC_250 = 'FC_250',
    FC_300 = 'FC_300',
    FC_400 = 'FC_400',
    FC_500 = 'FC_500',
}

export enum FbaTransportV2024ShipmentSourceType {
    SELLER_FACILITY = 'SELLER_FACILITY',
}

export enum FbaTransportV2024ShipmentStatus {
    ABANDONED = 'ABANDONED',
    CANCELLED = 'CANCELLED',
    CHECKED_IN = 'CHECKED_IN',
    CLOSED = 'CLOSED',
    DELETED = 'DELETED',
    DELIVERED = 'DELIVERED',
    IN_TRANSIT = 'IN_TRANSIT',
    MIXED = 'MIXED',
    READY_TO_SHIP = 'READY_TO_SHIP',
    RECEIVING = 'RECEIVING',
    SHIPPED = 'SHIPPED',
    UNCONFIRMED = 'UNCONFIRMED',
    WORKING = 'WORKING',
}

export enum FbaTransportV2024ShipmentDestinationType {
    AMAZON_OPTIMIZED = 'AMAZON_OPTIMIZED',
    AMAZON_WAREHOUSE = 'AMAZON_WAREHOUSE',
}

export interface FbaTransportV2024Weight {
    unit: FbaTransportV2024UnitOfWeight
    value: number
}

export interface FbaTransportV2024Dimensions {
    height: number
    length: number
    width: number
    unitOfMeasurement: FbaTransportV2024UnitOfMeasurement
}

export interface FbaTransportV2024ShipmentContactInformation {
    email: string
    name: string
    phoneNumber: string
}

export interface FbaTransportV2024Address {
    addressLine1: string
    addressLine2: string | null
    city: string | null
    companyName: string | null
    countryCode: string
    districtOrCounty: string | null
    email: string | null
    name: string
    phoneNumber: string | null
    postalCode: string
    stateOrProvinceCode: string | null
}

export interface FbaTransportV2024ShipmentDestination {
    address: FbaTransportV2024Address | null
    destinationType: FbaTransportV2024ShipmentDestinationType
    warehouseId: string | null
}

export interface FbaTransportV2024ShipmentFreightInformation {
    freightClass: FbaTransportV2024ShipmentFreightClass | null
    declaredValue: {
        amount: string
        code: string
    } | null
}

export interface FbaTransportV2024AppointmentSlotTime {
    endDate: string
    startDate: string
}

export interface FbaTransportV2024ShipmentSource {
    address: FbaTransportV2024Address | null
    sourceType: FbaTransportV2024ShipmentSourceType
}

export interface FbaTransportV2024BoxGroupItem {
    createdAt: string
    id: string
    planItemId: string
    quantity: number
}

export interface FbaTransportV2024BoxGroup {
    id: string
    lengthMm: number
    widthMm: number
    heightMm: number
    weightGm: number
    numberOfBoxes: number
    items: FbaTransportV2024BoxGroupItem[]
    boxNumbers: number[]
    packingGroupId?: string
}

// null is a shipment box group the server could not resolve: Amazon's boxes match none of the plan's box groups.
export type FbaTransportV2024ShipmentBoxGroupEntry = FbaTransportV2024BoxGroup | null;

export interface FbaTransportV2024ShipmentBox {
    id: string
    boxGroupId: string
    amazonBoxId: string | null
    packageId?: string
    boxNumber: number
}

export interface FbaTransportV2024ShipmentItem extends FbaTransportV2024PackingGroupItem {
    id: string
    planItemId: string
    shipmentId: string
    quantity: number
}

export interface FbaTransportV2024Shipment extends FbaTransportV2024PackingGroup {
    id: string
    contactInformation?: {
        email: string | null
        name: string | null
        phoneNumber: string | null
    }
    firstAvailabilityDate?: string | null
    transportModePreference?: FbaTransportV2024ShipmentGroup | null
    destination: FbaTransportV2024ShipmentDestination
    freightInformation?: FbaTransportV2024ShipmentFreightInformation
    selectedDeliveryWindow: string | null
    selectedTransportationOptionId: string | null
    shipmentConfirmationId: string | null
    shipmentId: string
    source?: FbaTransportV2024ShipmentSource
    status?: FbaTransportV2024ShipmentStatus | null
    boxGroups: FbaTransportV2024ShipmentBoxGroupEntry[]
    boxes: FbaTransportV2024ShipmentBox[]
    items: FbaTransportV2024ShipmentItem[]
    pallets: FbaTransportV2024Pallet[]
    deliveryWindowOptions: FbaTransportV2024DeliveryWindowOption[]
    transportationOptions: FbaTransportV2024TransportationOption[]
    trackingDetails: {
        ltlTrackingDetail: {
            billOfLadingNumber: string | null
            freightBillNumber: string[] | null
        } | null
        spdTrackingDetail: {
            spdTrackingItems: {
                boxId: string | null
                trackingId: string | null
                trackingNumberValidationStatus: string | null
            }[] | null
        } | null
    } | null
    amazonReferenceId: string | null
}

export interface AmazonMarketplace {
    identifier: string
    country: string
}

export interface FbaTransportV2024ItemEligibility {
    id: string
    channelId: number
    asin: string
    program: 'INBOUND'
    isEligible: boolean
    ineligibilityReasons?: string[] | null
}

export const FBA_INELIGIBILITY_REASONS: Record<string, string> = {
    FBA_INB_0004: 'Missing package dimensions. This product is missing necessary information; dimensions need to be provided in the manufacturer\'s original packaging.',
    FBA_INB_0006: 'The SKU for this product is unknown or cannot be found.',
    FBA_INB_0007: 'Product Under Dangerous Goods (Hazmat) Review. We do not have enough information to determine what the product is or comes with to enable us to complete our dangerous goods review.',
    FBA_INB_0008: 'Product Under Dangerous Goods (Hazmat) Review. We require detailed battery information to correctly classify the product.',
    FBA_INB_0009: 'Product Under Dangerous Goods (Hazmat) Review. We do not have enough dangerous goods information to correctly classify the product.',
    FBA_INB_0010: 'Product Under Dangerous Goods (Hazmat) Review. The dangerous goods information is mismatched and so the product cannot be correctly classified.',
    FBA_INB_0011: 'Product Under Dangerous Goods (Hazmat) Review. We have incomplete, inaccurate or conflicting dangerous goods information.',
    FBA_INB_0012: 'Product Under Dangerous Goods (Hazmat) Review. We have determined there is conflicting product information within the product detail pages.',
    FBA_INB_0013: 'Product Under Dangerous Goods (Hazmat) Review. Additional information is required in order to complete the Hazmat review process.',
    FBA_INB_0014: 'Product Under Dangerous Goods (Hazmat) Review. The product has been identified as possible dangerous goods.',
    FBA_INB_0015: 'Dangerous goods (Hazmat). The product is regulated as unfulfillable and not eligible for sale with Amazon.',
    FBA_INB_0016: 'Dangerous goods (Hazmat). The product is regulated as a fulfillable dangerous good. You may need to be in the FBA dangerous good (Hazmat) program.',
    FBA_INB_0017: 'This product does not exist in the destination marketplace catalog.',
    FBA_INB_0018: 'Product missing category. This product must have a category specified before it can be sent to Amazon.',
    FBA_INB_0019: 'This product must have a title before it can be sent to Amazon.',
    FBA_INB_0034: 'Product cannot be stickerless, commingled. This product must be removed.',
    FBA_INB_0035: 'Expiration-dated/lot-controlled product needs to be labeled.',
    FBA_INB_0036: 'Expiration-dated or lot-controlled product needs to be commingled. This product cannot be shipped to Amazon without being commingled.',
    FBA_INB_0037: 'This product is not eligible to be shipped to our fulfillment center. You do not have all the required tax documents.',
    FBA_INB_0038: 'Parent ASIN cannot be fulfilled by Amazon. You can send this product by creating a listing against the child ASIN.',
    FBA_INB_0050: 'There is currently no fulfillment center in the destination country capable of receiving this product.',
    FBA_INB_0051: 'This product has been blocked by FBA and cannot currently be sent to Amazon for fulfillment.',
    FBA_INB_0053: 'Product is not eligible in the destination marketplace. The required shipping option is not available or the product is too large or too heavy.',
    FBA_INB_0055: 'Product unfulfillable due to media region restrictions.',
    FBA_INB_0056: 'Product is ineligible for inbound. Used non-media goods cannot be shipped to Amazon.',
    FBA_INB_0059: 'Unknown Exception. This product must be removed at this time.',
    FBA_INB_0065: 'Product cannot be stickerless, commingled. This product must be removed.',
    FBA_INB_0066: 'Unknown Exception. This product must be removed at this time.',
    FBA_INB_0067: 'Product ineligible for freight shipping. This item is ineligible for freight shipping with our Global Shipping Service.',
    FBA_INB_0068: 'Account not configured for expiration-dated or lot-controlled products.',
    FBA_INB_0095: 'The barcode (UPC/EAN/JAN/ISBN) for this product is associated with more than one product in our fulfillment system.',
    FBA_INB_0097: 'Fully regulated dangerous good.',
    FBA_INB_0098: 'Merchant is not authorized to send item to destination marketplace.',
    FBA_INB_0099: 'Seller account previously terminated.',
    FBA_INB_0100: 'You do not have the required tax information to send inventory to fulfillment centers in Mexico.',
    FBA_INB_0103: 'This is an expiration-dated/lot-controlled product that cannot be handled at this time.',
    FBA_INB_0104: 'Item Requires Manufacturer Barcode. Only NEW products can be stored in our fulfillment centers without product labels.',
    FBA_INB_0197: 'Item requires safety and compliance documentation.',
    FBA_INB_0342: 'This offer is no longer eligible for sending to Amazon as it is using a manufacturer barcode that has been removed from the ASIN.',
    FBA_INB_0465: 'This product is not eligible to use manufacturer barcode as you are not identified as the brand representative for this product in Amazon brand registry: https://sell.amazon.com/brand-registry. This product must be removed from the shipment plan. You can send in new inventory by creating a new listing for this product by applying Amazon barcode labels.',
    UNKNOWN_INB_ERROR_CODE: 'Unknown Ineligibility Reason.',
};
